/**
 * Phase-sync athlete drive — how the athlete video follows the template video
 * when the two are aligned phase by phase.
 *
 * Background (prod report 2026-11-02, Marcel-Lunge on New Lunge): the viewer
 * used to PLAY the athlete video natively AND hard-seek it on every template
 * `timeupdate` to the phase-mapped target. With a timing ratio near 1 the two
 * drivers agree and nothing is visible. With a ratio far from 1 (Marcel's
 * Execution is 0.31× Bart's duration) native playback outruns the target, every
 * tick snaps the athlete backward, and the athlete appears to loop a few frames
 * until the template finishes — then plays the rest natively.
 *
 * The fix: while playing in phase mode the athlete's `playbackRate` is the
 * viewer's speed × the current phase's timing ratio (athlete duration ÷
 * template duration), so it advances at the aligned pace on its own, and a seek
 * is issued only when it drifts beyond a tolerance. While paused (scrubbing,
 * phase slider, frame stepping) the exact seek is kept.
 */

/** Browser-safe playbackRate window (Chrome accepts 0.0625–16; Safari is
 * narrower, so stay well inside and let drift correction cover the rest). */
export const PHASE_SYNC_MIN_RATE = 0.25;
export const PHASE_SYNC_MAX_RATE = 4;
/** Seconds of athlete drift from the phase-mapped target tolerated before a
 * corrective seek. ~3.5 frames at 30 fps — below it a seek would cause more
 * visible stutter than the drift it fixes. */
export const PHASE_SYNC_DRIFT_TOLERANCE_S = 0.12;

export interface AthleteDriveInput {
  /** The template (and so the athlete) is playing, not paused/scrubbing. */
  playing: boolean;
  /** Viewer playback speed (1 = real time). */
  playbackSpeed: number;
  /** Current phase's timing ratio = athlete phase duration ÷ template phase duration. */
  timingRatio: number;
  /** Athlete video's current media time, seconds. */
  athleteTime: number;
  /** Phase-mapped target athlete time for the template's current time, seconds. */
  targetTime: number;
}

export interface AthleteDrivePlan {
  /** playbackRate to apply to the athlete video. */
  rate: number;
  /** Media time to seek to, or null when the athlete is close enough. */
  seekTo: number | null;
}

export function clampPlaybackRate(rate: number): number {
  if (!Number.isFinite(rate) || rate <= 0) return 1;
  return Math.min(PHASE_SYNC_MAX_RATE, Math.max(PHASE_SYNC_MIN_RATE, rate));
}

export function planAthleteDrive({ playing, playbackSpeed, timingRatio, athleteTime, targetTime }: AthleteDriveInput): AthleteDrivePlan {
  const speed = Number.isFinite(playbackSpeed) && playbackSpeed > 0 ? playbackSpeed : 1;
  if (!playing) {
    // Paused / scrubbing: exact position, viewer speed restored for the next play.
    return { rate: speed, seekTo: targetTime };
  }
  const ratio = Number.isFinite(timingRatio) && timingRatio > 0 ? timingRatio : 1;
  const rate = clampPlaybackRate(speed * ratio);
  const drift = Math.abs(athleteTime - targetTime);
  return { rate, seekTo: drift > PHASE_SYNC_DRIFT_TOLERANCE_S ? targetTime : null };
}
